import type { DataSourceLoadOptions, RecommendationToken, ReviewToken } from '@sleed/bizcommerce-api';
import { BizwebEntityType, ReviewReportReason } from '@sleed/bizcommerce-api';
import type { PillVariant } from '~/components/pill';
import type { GridSort } from '~/lib/grid/sort';
import type { CsvColumn } from '~/lib/export-csv';
import { formatDateTime } from '~/lib/format-date';

/** Which review surface — customer comments vs contributor (editorial) reviews (separate legacy endpoints). */
export type ReviewKind = 'customer' | 'contributor';

/**
 * Grid column → server sort selector. The selectors are the keys of ReviewsController's `_AllowedFilterMappings`
 * (anything else is dropped server-side); with no sort the server lists the newest review_date first.
 */
export const REVIEW_SORT_SELECTORS = {
	rating: 'rating',
	status: 'st',
	date: 'review_date',
	reviewer: 'reviewer',
	source: 'source',
	title: 'tl',
	sort: 'si',
	featured: 'isFeatured',
	visible: 'iv',
	created: 'cdt',
	id: 'id',
	helpful: 'helpful_count'
} as const;

export type ReviewSortKey = keyof typeof REVIEW_SORT_SELECTORS;

/** Columns whose header sorts, per tab: customer rows have no `reviewer`/`source` text (those are editorial fields). */
export const SORTABLE_REVIEW_COLUMNS: Record<ReviewKind, ReviewSortKey[]> = {
	customer: ['rating', 'status', 'date', 'helpful'],
	contributor: ['reviewer', 'rating', 'status', 'date', 'helpful']
};

export function isReviewSortKey(key: string): key is ReviewSortKey {
	return Object.prototype.hasOwnProperty.call(REVIEW_SORT_SELECTORS, key);
}

/**
 * The `sort` part of the grid's loadOptions. The reviews grid POSTs loadOptions as a JSON body, so a real array works
 * here (unlike the GET grids that need `applySort`'s JSON string). Unknown keys send no sort (server default).
 */
export function reviewSortOptions(sort: GridSort | null): Pick<DataSourceLoadOptions, 'sort'> {
	if (!sort || !isReviewSortKey(sort.key)) return {};
	return { sort: [{ selector: REVIEW_SORT_SELECTORS[sort.key], desc: sort.dir === 'desc' }] };
}

/**
 * Who wrote the review, from its `from_ent` (a contributor = editorial, else a customer); the tab it is listed on when
 * the row does not say.
 */
export function reviewAuthorKind(kind: ReviewKind, r: Pick<ReviewToken, 'from_ent'>): ReviewKind {
	if (r.from_ent === BizwebEntityType.Contributor) return 'contributor';
	if (r.from_ent === BizwebEntityType.Customer) return 'customer';
	return kind;
}

/** Name shown in the reviewer column: the customer, or the contributor (else the free-text reviewer of an editorial review). */
export function reviewerName(kind: ReviewKind, r: ReviewToken): string {
	if (reviewAuthorKind(kind, r) === 'customer') return `${r.cust_fn ?? ''} ${r.cust_ln ?? ''}`.trim();
	return `${r.con_fn ?? ''} ${r.con_ln ?? ''}`.trim() || (r.reviewer ?? '').trim();
}

/**
 * What each tab's reviews can be about (the server's review targets): customers review products, contributors and
 * brands; editorial reviews may also cover a content page or a customer. The target-type filter offers these.
 */
export const REVIEW_TARGET_TYPES: Record<ReviewKind, BizwebEntityType[]> = {
	customer: [BizwebEntityType.Product, BizwebEntityType.Contributor, BizwebEntityType.Manufacturers],
	contributor: [
		BizwebEntityType.Product,
		BizwebEntityType.Contributor,
		BizwebEntityType.Manufacturers,
		BizwebEntityType.ContentPage,
		BizwebEntityType.Customer
	]
};

/**
 * Name of the reviewed item; `#<id>` when the item was deleted (the grids still list its reviews, with no name), and
 * "—" only without an id either.
 */
export function reviewTargetName(name: string | null | undefined, toId: number | null | undefined): string {
	return name?.trim() || (toId ? `#${toId}` : '—');
}

/** A review without a target type predates the other targets: it is about a product. */
export function isProductTarget(toEnt: BizwebEntityType | null | undefined): boolean {
	return toEnt == null || toEnt === BizwebEntityType.Product;
}

/** Locale key of a review target type (the filter options, and the badge when the server sends no name). */
export function reviewTargetTypeKey(toEnt: BizwebEntityType | null | undefined): string {
	switch (toEnt ?? BizwebEntityType.Product) {
		case BizwebEntityType.Product:
			return 'catalog.reviews.targets.product';
		case BizwebEntityType.Contributor:
			return 'catalog.reviews.targets.contributor';
		case BizwebEntityType.Manufacturers:
			return 'catalog.reviews.targets.brand';
		case BizwebEntityType.ContentPage:
			return 'catalog.reviews.targets.contentPage';
		case BizwebEntityType.Customer:
			return 'catalog.reviews.targets.customer';
		default:
			return 'catalog.reviews.targets.other';
	}
}

/** The type badge's text: the server's localized name (`to_ent_nm`), else ours. */
export function reviewTargetTypeLabel(
	t: (key: string) => string,
	toEnt: BizwebEntityType | null | undefined,
	serverName?: string | null
): string {
	return serverName?.trim() || t(reviewTargetTypeKey(toEnt));
}

/** Badge colour per target type: products stay quiet (most rows), the other kinds stand out. */
export function reviewTargetVariant(toEnt: BizwebEntityType | null | undefined): PillVariant {
	switch (toEnt ?? BizwebEntityType.Product) {
		case BizwebEntityType.Product:
			return 'neutral';
		case BizwebEntityType.Contributor:
			return 'primary';
		default:
			return 'info';
	}
}

/**
 * Storefront link of a reviewed item: the deploy's storefront origin + the item's site-relative URL (`eurl`). An
 * absolute `eurl` is used as is; nothing when either part is missing (no storefront configured, item without URL).
 */
export function storefrontUrl(storefrontBase: string | undefined, eurl: string | null | undefined): string | undefined {
	const path = eurl?.trim();
	if (!path) return undefined;
	if (/^https?:\/\//i.test(path)) return path;
	const base = storefrontBase?.trim();
	if (!base) return undefined;
	return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

type Translate = (key: string) => string;

const yesNo = (t: Translate, value: boolean | null | undefined) => (value ? t('common.yes') : t('common.no'));

/** CSV columns of the reviews export — the grid's columns plus the fields the legacy Excel export carried. */
export function reviewExportColumns(kind: ReviewKind, t: Translate): CsvColumn<ReviewToken>[] {
	const columns: CsvColumn<ReviewToken>[] = [
		{ header: 'ID', value: r => r.id },
		{ header: t('catalog.reviews.export.targetType'), value: r => reviewTargetTypeLabel(t, r.to_ent, r.to_ent_nm) },
		{ header: t('catalog.reviews.export.targetCode'), value: r => r.to_code },
		{ header: t('catalog.reviews.columns.target'), value: r => reviewTargetName(r.to_title, r.to_id) },
		{ header: t('catalog.reviews.columns.reviewer'), value: r => reviewerName(kind, r) }
	];
	if (kind === 'customer') {
		columns.push({ header: t('catalog.reviews.export.email'), value: r => r.cust_eml });
	} else {
		columns.push(
			{ header: t('catalog.reviews.fields.category'), value: r => r.cat_name },
			{ header: t('catalog.reviews.fields.source'), value: r => r.source }
		);
	}
	columns.push(
		{ header: t('catalog.reviews.columns.rating'), value: r => r.rating },
		{ header: t('catalog.reviews.columns.status'), value: r => r.st_nm },
		{ header: t('catalog.reviews.columns.date'), value: r => formatDateTime(r.review_date) },
		{ header: t('catalog.reviews.title_field'), value: r => r.tl },
		{ header: t('catalog.reviews.comment'), value: r => r.review },
		{ header: t('catalog.reviews.columns.visible'), value: r => yesNo(t, r.iv) },
		{ header: t('catalog.reviews.featured'), value: r => yesNo(t, r.isFeatured) },
		{ header: t('catalog.reviews.columns.helpful'), value: r => r.helpful_count ?? 0 },
		{ header: t('catalog.reviews.columns.openReports'), value: r => r.open_reports ?? 0 }
	);
	return columns;
}

/**
 * The "to" date filter covers the whole picked day: the date input gives local midnight, which the server's
 * `date <= to` read as "before that day" (the day's own reviews were left out).
 */
export function endOfDayIso(iso: string): string {
	const date = new Date(iso);
	date.setHours(23, 59, 59, 999);
	return date.toISOString();
}

/** Recommendation tabs — customer "I recommend it" vs contributor recommendations (separate legacy endpoints). */
export type RecommendationKind = 'customer' | 'contributor';

/** Who recommended: the public name the server sends, else the id (the admin grid has no other customer field). */
export function recommenderName(r: RecommendationToken): string {
	return (r.from_name ?? '').trim() || (r.from_id != null ? `#${r.from_id}` : '');
}

/** Locale key of a report reason (ReviewReportReason: Spam=1, Offensive=2, OffTopic=3, Misleading=4, Other=5). */
export function reportReasonKey(reason: ReviewReportReason | null | undefined): string {
	switch (reason) {
		case ReviewReportReason.Spam:
			return 'catalog.reviews.reports.reasons.spam';
		case ReviewReportReason.Offensive:
			return 'catalog.reviews.reports.reasons.offensive';
		case ReviewReportReason.OffTopic:
			return 'catalog.reviews.reports.reasons.offTopic';
		case ReviewReportReason.Misleading:
			return 'catalog.reviews.reports.reasons.misleading';
		default:
			return 'catalog.reviews.reports.reasons.other';
	}
}

/** The bulk endpoints take at most this many ids per call. */
export const REVIEW_BULK_LIMIT = 500;

/** Splits ids into calls of at most `size` (the server's bulk cap). */
export function chunkIds(ids: number[], size = REVIEW_BULK_LIMIT): number[][] {
	const chunks: number[][] = [];
	for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
	return chunks;
}

/** Select-all on the page: selects every row of the page, or clears them when all are already selected. */
export function togglePageSelection(selected: ReadonlySet<number>, pageIds: number[]): Set<number> {
	const next = new Set(selected);
	const allSelected = pageIds.length > 0 && pageIds.every(id => next.has(id));
	for (const id of pageIds) {
		if (allSelected) next.delete(id);
		else next.add(id);
	}
	return next;
}
